import { Box } from "@mui/material";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import { KPI_META, type KpiKey } from "../kpiMeta";
import {
  buildStatisticsQueryFilters,
  type KpiBreakdownData,
  type StatisticsFilters,
} from "../types";
import { KpiCategorySubcategoryPie } from "./KpiCategorySubcategoryPie";
import { KpiDialogScaffold } from "./KpiDialogScaffold";
import { KpiPieChart } from "./KpiPieChart";

interface KpiBreakdownDialogProps {
  open: boolean;
  onClose: () => void;
  kpiKey: KpiKey | null;
  organizationId: string | undefined;
  filters: StatisticsFilters;
}

export const KpiBreakdownDialog: FC<KpiBreakdownDialogProps> = ({
  open,
  onClose,
  kpiKey,
  organizationId,
  filters,
}) => {
  const meta = kpiKey ? KPI_META[kpiKey] : null;

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "statistics-kpi-breakdown",
      organizationId,
      filters,
      kpiKey,
    ],
    queryFn: async () => {
      const { data: payload } = await linko.statistics.queries.getKpiBreakdown({
        kpiKey: kpiKey!,
        ...buildStatisticsQueryFilters(organizationId!, filters),
      });
      return payload as KpiBreakdownData;
    },
    enabled: open && !!organizationId && !!kpiKey,
    staleTime: 60_000,
  });

  return (
    <KpiDialogScaffold
      open={open}
      onClose={onClose}
      title={meta?.dialogTitle ?? "פירוט"}
      explanation={meta?.explanation ?? ""}
      isLoading={isLoading}
      error={error}
      footerNote="אחוזים ביחס לסכום המקטעים בתרשים. משימה עם קישורים למספר אוכלוסיות/פרויקטים/קטגוריות נספרת בכמה מקטעים."
    >
      {data && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: 2, lg: 2 },
              alignItems: "start",
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
              <KpiPieChart
                title="לפי אוכלוסייה"
                data={data.populations.map((p) => ({
                  name: p.name,
                  value: p.count,
                }))}
              />
            </Box>
            <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
              <KpiPieChart
                title="לפי פרויקט"
                data={data.teams.map((t) => ({
                  name: t.name,
                  value: t.count,
                }))}
              />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                gridColumn: { xs: "1", lg: "auto" },
              }}
            >
              <KpiCategorySubcategoryPie
                title="לפי תת־קטגוריה (צבע לפי קטגוריה)"
                rows={data.categorySubcategories.map((r) => ({
                  categoryId: r.categoryId,
                  categoryName: r.categoryName,
                  subCategoryName: r.subCategoryName,
                  count: r.count,
                }))}
              />
            </Box>
          </Box>
      )}
    </KpiDialogScaffold>
  );
};
